/**
 * Pure helpers for the monetic bulk archive/unarchive flow.
 *
 * Mirrors the printer archive-batch flow so both peripherals behave uniformly.
 * The backend owns the business rule "a monetic associated with an application
 * cannot be archived": the front sends the whole selection to
 * `POST /settings/monetics/archive-batch` and reads each item's outcome from the
 * `succeeded[]` / `failed[]` arrays of the response.
 *
 * Everything here is framework-free so it can be unit-tested without rendering.
 */

/** `typeDisplay` values driving the active vs archived monetic list. */
export const TYPE_DISPLAY_NOT_ARCHIVED = "notArchived";
export const TYPE_DISPLAY_ARCHIVED = "archived";

/** Reason flag returned by the backend for a monetic it refused to archive. */
export const ASSOCIATED_WITH_APP_REASON = "ASSOCIATED_WITH_APP";

/** A monetic row coming from the table selection. */
export interface SelectedMonetic {
    Id: string;
    shopId: string;
    userID: number | string;
    franchiseID: string;
}

/** One entry of the request payload sent to archive-batch. */
export interface ArchiveBatchItem {
    shopId: string;
    moneticId: string;
}

/** Request body of `POST /settings/monetics/archive-batch`. */
export interface ArchiveBatchRequest {
    userId: number | string;
    franchiseId: string;
    operatorId: string;
    uuidUser: string | null;
    isArchived: boolean;
    items: ArchiveBatchItem[];
}

/** One result entry returned by the backend. */
export interface ArchiveBatchResultItem {
    shopId: string;
    moneticId: string;
    reason?: string;
}

/** Response envelope of archive-batch. */
export interface ArchiveBatchResponse {
    error?: boolean;
    data?: {
        totalRequested?: number;
        totalSucceeded?: number;
        totalFailed?: number;
        succeeded?: ArchiveBatchResultItem[];
        failed?: ArchiveBatchResultItem[];
    };
}

/** Aggregated outcome the UI turns into toasts. */
export interface ArchiveBatchOutcome {
    archived: number;
    associatedSkipped: number;
    otherFailures: number;
    /** True when the response does not match the expected contract. */
    malformed: boolean;
}

/** Build the archive-batch request body from the current selection. */
export function buildArchiveBatchPayload(
    rows: SelectedMonetic[],
    options: {
        uuidUser: string | null;
        isArchived: boolean;
        operatorId: string;
    }
): ArchiveBatchRequest {
    const [first] = rows;
    return {
        userId: first.userID,
        franchiseId: first.franchiseID,
        operatorId: options.operatorId,
        uuidUser: options.uuidUser,
        isArchived: options.isArchived,
        items: rows.map((row) => ({
            shopId: row.shopId,
            moneticId: row.Id,
        })),
    };
}

/**
 * Map a (possibly partial) archive-batch response to counts.
 *
 * `malformed` is set when neither `succeeded` nor `failed` is an array — i.e.
 * the backend deviated from the contract — so the caller can surface an error
 * instead of failing silently.
 */
export function mapArchiveBatchResult(
    response: ArchiveBatchResponse | null | undefined
): ArchiveBatchOutcome {
    const data = response?.data;
    const hasSucceeded = Array.isArray(data?.succeeded);
    const hasFailed = Array.isArray(data?.failed);
    const succeeded = hasSucceeded ? data!.succeeded! : [];
    const failed = hasFailed ? data!.failed! : [];

    const associatedSkipped = failed.filter(
        (item) => item.reason === ASSOCIATED_WITH_APP_REASON
    ).length;

    return {
        archived: succeeded.length,
        associatedSkipped,
        otherFailures: failed.length - associatedSkipped,
        malformed: !hasSucceeded && !hasFailed,
    };
}
